import { useCallback, useEffect, useState } from "react";
import type { FlexProps } from "@chakra-ui/react";
import { Flex, Text, useMediaQuery, useMultiStyleConfig, useTheme } from "@chakra-ui/react";
import { FxBreakdownView } from "./FxBreakdownView";
import { AccessibleSelect } from "@/components/AccessibleSelect/AccessibleSelect";
import { AsOfDateLabel } from "@/components/AsOfDateLabel/AsOfDateLabel";
import { ContentBlock } from "@/components/ContentBlock/ContentBlock";
import type { ContentGroupProps } from "@/components/ContentGroup/ContentGroup";
import { ContentGroup } from "@/components/ContentGroup/ContentGroup";
import { useInstanceConfig } from "@/services/InstanceConfigContext";
import { htmlify, formatHypothetical, interpolatePlaceholders } from "@/services/utils";
import type { FundDetailLayoutComponentProps } from "@/types/fundDetailLayout";
import type { V1BreakdownFormatConfig } from "@/types/v1";

export interface FundBreakdownPanelProps extends Omit<FlexProps, "color"> {
	heading?: string;
	sections: FundBreakdownSectionProps[];
	shortLabel?: string;
	longLabel?: string;
	netLabel?: string;
	currentWeightLabel?: string;
	minimumThresholdLabel?: string;
	actualThresholdLabel?: string;
	fundLabel?: string;
	benchmarkLabel?: string;
	differenceLabel?: string;
	disclaimer?: string;
	weightingColor?: string;
	asOfLabel?: string;
	textAlignment?: string;
	selectLabel?: string;
	componentDisclaimer?: string;
}

export interface FundBreakdownSectionProps extends FlexProps {
	id: string;
	name: string;
	asOf: string;
	category: string;
	options?: {
		format: V1BreakdownFormatConfig;
		forceCompact?: boolean;
	};
	headerConfig?: {
		column: string;
		label: string;
	}[];
	tableRowConfig?: {
		field: string;
		width: string;
	}[];
	disclaimer?: string;
	items: {
		id: string;
		name: string;
		weighting: number;
		weightingRange: string;
		weightingFormatted: string;
		weightingShort?: number;
		weightingShortFormatted?: string;
		weightingLong?: number;
		weightingLongFormatted?: string;
		weightingNet?: number;
		weightingNetFormatted?: string;
		weightingPrevious?: number;
		weightingPreviousFormatted?: string;
		weightingDifference?: number;
		weightingDifferenceFormatted?: string;
		weightingEquity?: number;
		weightingEquityFormatted?: string;
		weightingFixedIncome?: number;
		weightingFixedIncomeFormatted?: string;
		expectedRange?: number;
		potentialRange?: number;
		sinceLaunchLow?: number;
		sinceLaunchHigh?: number;
		threshold?: number;
		thresholdFormatted?: string;
		role?: string;
		benchmark?: {
			id: string;
			name: string;
			weighting: number;
			weightingFormatted: string;
		};
		difference?: {
			weighting: number;
			weightingFormatted: string;
		};
		color?: string;
	}[];
	keys: {
		value: string;
		label: string;
		weighting: string;
		color: string;
	}[];
	viewAllButtonText: string;
}

export const FxBreakdownPanel = ({ dashboard, unitType, unitValue, unitCurrency }: FundDetailLayoutComponentProps) => {
	const styleParts = useMultiStyleConfig("FxStyles");

	const instanceConfig = useInstanceConfig();
	const theme = useTheme();
	const { fxBreakdownPanel: fundBreakdownProps, fxProfileHeroPanel: profileHeroProps } = dashboard;
	const { heading, sections, disclaimer, selectLabel, componentDisclaimer, ...rest } = fundBreakdownProps || {};
	const [allocationType, setAllocationType] = useState<string | undefined>(() => {
		return sections ? sections[0].id : undefined;
	});
	const [section, setSection] = useState<FundBreakdownSectionProps | undefined>(() => {
		return sections ? sections[0] : undefined;
	});
	const [isMobile] = useMediaQuery(`(max-width: ${theme?.breakpoints?.md || 320})`);

	const onFormatHypothetical = useCallback(
		(value: unknown) => {
			return formatHypothetical({
				value,
				unitType,
				unitValue,
				unitCurrency,
				format: {
					type: "number",
					decimalPlaces: 2,
					includePercentageSymbol: true
				},
				instanceConfig: instanceConfig
			});
		},
		[unitType, unitValue, unitCurrency, instanceConfig]
	);

	useEffect(() => {
		if (!allocationType || !sections) {
			return;
		}

		const section = sections.find(({ id }) => {
			return allocationType === id;
		});
		setSection(section);
	}, [sections, allocationType]);

	if (!fundBreakdownProps) {
		return null;
	}

	return (
		<ContentGroup
			id="fxBreakdownPanel"
			data-testid="FxBreakdownPanel"
			tabIndex={-1}
			{...(styleParts.breakdownPanelWrapper as ContentGroupProps)}
		>
			<ContentBlock
				heading={htmlify(heading)}
				// subHeading={}
			>
				{Boolean(componentDisclaimer) && (
					<Flex {...(styleParts.componentDisclaimer as FlexProps)}>
						{htmlify(interpolatePlaceholders({ text: componentDisclaimer, instanceConfig }))}
					</Flex>
				)}

				{allocationType !== undefined && sections !== undefined && (
					<AccessibleSelect
						aria-label={selectLabel ? selectLabel : "Category"}
						label={selectLabel ?? "Category"}
						id="breakdown-panel"
						variant="brandOutline"
						maxWidth="550px"
						minHeight="50px"
						fontWeight="bold"
						whiteSpace={{ base: "break-spaces", md: "unset" }}
						value={allocationType}
						onChange={(e) => {
							setAllocationType(e.target.value);
						}}
					>
						{sections.map(({ id, name }) => {
							return (
								<option style={{ color: "black !important", backgroundColor: "white !important" }} key={id} value={id}>
									{htmlify(name)}
								</option>
							);
						})}
					</AccessibleSelect>
				)}

				{section && <AsOfDateLabel label={fundBreakdownProps.asOfLabel} date={section?.asOf} />}

				{Boolean(section?.disclaimer) && (
					<Flex {...(styleParts.componentDisclaimer as FlexProps)}>
						{htmlify(interpolatePlaceholders({ text: section?.disclaimer, instanceConfig }))}
					</Flex>
				)}

				{section && (
					<FxBreakdownView
						section={section}
						formatHypothetical={onFormatHypothetical}
						{...rest}
						variant={isMobile || section?.options?.forceCompact === true ? "compact" : undefined}
					/>
				)}

				{Boolean(disclaimer) && (
					<Flex fontSize={13} lineHeight="medium" p={{ color: "inherit" }} marginTop={2} flexDirection="column">
						{htmlify(interpolatePlaceholders({ text: disclaimer, instanceConfig }))}
					</Flex>
				)}
			</ContentBlock>
		</ContentGroup>
	);
};
